import { createSlice } from "@reduxjs/toolkit";

import { defaultState } from "./defaultState";
import { getStoredState } from "../storedState";

import { BFC_SPA_Uterum } from "../../constants/constants";

import { requestSize } from "../requests/thunks/requestSize";
import { requestSizeUpdate } from "../requests/thunks/requestSizeUpdate";
import { requestDesign_Aggregated } from "../requests/thunks/requestDesign_Aggregated";
import { requestDesignUpdate } from "../requests/thunks/requestDesignUpdate";
import { requestSummary_Aggregated } from "../requests/thunks/requestSummary_Aggregated";

import { getDefaultRoofForSeason } from "../../helpers/getRoofForSeason";

const storedState = getStoredState(BFC_SPA_Uterum);

const initialState = storedState || defaultState;

const handleUterumResponse = (state, action) => {
  const { cgeObj } = action.payload;
  const { Uterum } = cgeObj;

  const verifiedUterum = {
    ...Uterum,
    // Some properties can be omitted by CGE. In that case, set to null.
    ...(Uterum.FrameColor === undefined && { FrameColor: null }),
    ...(Uterum.FrameArticleErpId === undefined && { FrameArticleErpId: null }),
    ...(Uterum.FramePrimedArticleErpId === undefined && {
      FramePrimedArticleErpId: null,
    }),
    ...(Uterum.FramePrimedAddonArticleErpId === undefined && {
      FramePrimedAddonArticleErpId: null,
    }),
    ...(Uterum.FramePaintedArticleErpId === undefined && {
      FramePaintedArticleErpId: null,
    }),
    ...(Uterum.FramePaintedAddonArticleErpId === undefined && {
      FramePaintedAddonArticleErpId: null,
    }),
    ...(Uterum.VindskivaErpId === undefined && {
      VindskivaErpId: null,
    }),
    ...(Uterum.GroundErpId === undefined && {
      GroundErpId: null,
    }),
    ...(Uterum.WallProfileErpId === undefined && {
      WallProfileErpId: null,
    })
  };

  Object.keys(verifiedUterum).forEach((key) => {
    state[key] = verifiedUterum[key];
  });
};

const UterumSlice = createSlice({
  name: "Uterum",
  initialState,
  reducers: {
    setSeason(state, action) {
      const Season = action.payload;

      // (Re)set roof based on selected season
      const Roof = getDefaultRoofForSeason(Season);

      !Roof &&
        console.error(
          `UterumSlice: No Roof, action: ${JSON.stringify(
            action
          )}, current state: ${JSON.stringify(state)}`
        );

      // Always force default Color WHITE
      state.Color = "WHITE";

      // Always set PanelIsolation true for winter,
      // for all other seasons, always set false
      state.PanelIsolation = Season === "ISOMAX";

      state.Roof = Roof;
      state.Season = Season;
    },
    setFrameType(state, action) {
      const FrameType = action.payload;
      const { Season } = state;

      /*
      For these models, SelectedGroupArea has to be set to
      default value "ONSIDE_LEFT" (used in DesignView, step 4).
      This is, for some reason, not done by the CGE by default.
      */
      state.SelectedGroupArea =
        FrameType === "SKAPA" || FrameType === "ONSKA" || FrameType === "LYCKA"
          ? "ONSIDE_LEFT"
          : null;

      // (Re)set roof based on selected season
      const Roof = getDefaultRoofForSeason(Season);

      !Roof &&
        console.error(
          `UterumSlice: No Roof, action: ${JSON.stringify(
            action
          )}, current state: ${JSON.stringify(state)}`
        );

      /*
      Always reset default values for these properties when
      FrameType is set or updated.
      */
      state.Roof = Roof;
      state.FramePrimed = "YES";
      state.FrameColor = null; // Never set empty str!
      state.FrameArticleErpId = null;
      state.FramePrimedArticleErpId = null;
      state.FramePrimedAddonArticleErpId = null;
      state.FramePaintedArticleErpId = null;
      state.FramePaintedAddonArticleErpId = null;
      state.Corners = "NONE";
      state.FrameAreas = null;
      state.Partier = null;
      state.DrainArticleErpIds = [];
      state.Lameller = "";
      state.PaintArticleErpId = "";
      state.PaintPanelArticleErpId = "";
      state.FrameAddonWidth = null;
      state.VindskivaErpId = "";
      state.GroundErpId = "";
      state.WallProfileErpId = "";

      state.FrameType = FrameType;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestSize.fulfilled, handleUterumResponse)
      .addCase(requestSizeUpdate.fulfilled, handleUterumResponse)
      .addCase(requestDesign_Aggregated.fulfilled, handleUterumResponse)
      .addCase(requestDesignUpdate.fulfilled, handleUterumResponse)
      .addCase(requestSummary_Aggregated.fulfilled, handleUterumResponse);
  },
});

export const {
  setSeason,
  setFrameType,
  setFramePrimed,
  setFrameColor,
  setWidth,
  setDepth,
  setColor,
  toggleVindskiva,
  setGround,
  setLameller,
  setPaint,
  setRoof,
  setDrainArticleErpIds,
  setCorners,
  setFrameDepth,
  setFrameWidth,
  setFrameAddonWidth,
  setLfcConfig,
  setGroupArea,
  setFrameArea,
  toggleWindowPanel,
  togglePanelIsolation,
} = UterumSlice.actions;

export default UterumSlice.reducer;
